import Phaser from 'phaser';
import { sounds } from '../audio/SoundEffects';
import { progression } from '../game/Progression';
import { ShopModal } from '../ui/ShopModal';

export class GameOverScene extends Phaser.Scene {
  constructor() {
    super({ key: 'GameOverScene' });
  }

  init(data) {
    this.currentLevel = data.level || 1;
    this.finalScore = data.score || 0;
    this.finalVinyls = data.vinyls || 0;
    this.bankGain = data.bankGain || data.vinyls || 0;
    this.bankTotal = data.bankTotal || progression.getBankVinyls();
  }

  create() {
    const { width, height } = this.scale;

    // Background dark curtain
    const bg = this.add.graphics();
    bg.fillGradientStyle(0x18181b, 0x18181b, 0x09090b, 0x09090b, 1);
    bg.fillRect(0, 0, width, height);

    // Decorative thunder glow
    const glow = this.add.graphics();
    glow.fillStyle(0xef4444, 0.15);
    glow.fillCircle(width / 2, height / 2, 280);

    // Title: CONCERT OVER
    this.add.text(width / 2, 130, 'CONCERT OVER!', {
      fontFamily: 'Impact, Arial Black, sans-serif',
      fontSize: '44px',
      color: '#ef4444',
      stroke: '#450a0a',
      strokeThickness: 8,
      align: 'center'
    }).setOrigin(0.5);

    // Subtitle
    this.add.text(width / 2, 180, '"The music never truly dies!"', {
      fontFamily: 'Arial, sans-serif',
      fontSize: '15px',
      color: '#cbd5e1',
      fontStyle: 'italic',
      align: 'center'
    }).setOrigin(0.5);

    // Score Board Panel
    const panel = this.add.graphics();
    panel.fillStyle(0x1e293b, 0.92);
    panel.fillRoundedRect(width / 2 - 165, 215, 330, 230, 16);
    panel.lineStyle(3, 0xf59e0b, 0.85);
    panel.strokeRoundedRect(width / 2 - 165, 215, 330, 230, 16);

    this.add.text(width / 2, 250, `SCORE: ${this.finalScore}`, {
      fontFamily: 'Impact, Arial Black, sans-serif',
      fontSize: '32px',
      color: '#facc15'
    }).setOrigin(0.5);

    this.add.text(width / 2, 298, `📀 RUN REWARD: +${this.bankGain} VINYLS`, {
      fontFamily: 'Arial, sans-serif',
      fontSize: '16px',
      color: '#4ade80',
      fontStyle: 'bold'
    }).setOrigin(0.5);

    this.txtBank = this.add.text(width / 2, 345, `🏦 BANK VAULT: ${progression.getBankVinyls()} VINYLS`, {
      fontFamily: 'Arial, sans-serif',
      fontSize: '16px',
      color: '#ffffff',
      fontStyle: 'bold'
    }).setOrigin(0.5);

    const highScore = localStorage.getItem('zelvis_highscore') || '0';
    this.add.text(width / 2, 395, `🏆 ALL-TIME BEST: ${highScore}`, {
      fontFamily: 'Arial, sans-serif',
      fontSize: '15px',
      color: '#38bdf8',
      fontStyle: 'bold'
    }).setOrigin(0.5);

    // RETRY BUTTON
    const retryBtn = this.add.container(width / 2, height - 235);
    const rBg = this.add.rectangle(0, 0, 280, 56, 0xf59e0b, 1);
    rBg.setStrokeStyle(3, 0xfef08a, 1);
    rBg.setInteractive({ useHandCursor: true });

    const rTxt = this.add.text(0, 0, '⚡ ENCORE! (RETRY) ⚡', {
      fontFamily: 'Impact, Arial Black, sans-serif',
      fontSize: '22px',
      color: '#0f172a'
    }).setOrigin(0.5);

    retryBtn.add([rBg, rTxt]);

    const onRetry = () => {
      sounds.playRockChord();
      this.scene.start('GameScene', { level: this.currentLevel });
    };

    rBg.on('pointerdown', onRetry);
    rTxt.setInteractive({ useHandCursor: true }).on('pointerdown', onRetry);

    this.tweens.add({
      targets: retryBtn,
      scaleX: 1.05,
      scaleY: 1.05,
      duration: 700,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });

    // SHOP BUTTON
    const shopBtn = this.add.container(width / 2, height - 165);
    const sBg = this.add.rectangle(0, 0, 280, 48, 0xb45309, 1);
    sBg.setStrokeStyle(2, 0xfacc15, 1);
    sBg.setInteractive({ useHandCursor: true });

    const sTxt = this.add.text(0, 0, '🛍️ ROCKSTAR SHOP & PERKS', {
      fontFamily: 'Arial, sans-serif',
      fontSize: '15px',
      color: '#ffffff',
      fontStyle: 'bold'
    }).setOrigin(0.5);

    shopBtn.add([sBg, sTxt]);

    const onOpenShop = () => {
      new ShopModal(this, () => {
        if (this.txtBank) {
          this.txtBank.setText(`🏦 BANK VAULT: ${progression.getBankVinyls()} VINYLS`);
        }
      });
    };

    sBg.on('pointerdown', onOpenShop);
    sTxt.setInteractive({ useHandCursor: true }).on('pointerdown', onOpenShop);

    // MAIN MENU BUTTON
    const menuBtn = this.add.container(width / 2, height - 100);
    const mBg = this.add.rectangle(0, 0, 240, 44, 0x334155, 0.95);
    mBg.setStrokeStyle(2, 0x64748b, 1);
    mBg.setInteractive({ useHandCursor: true });

    const mTxt = this.add.text(0, 0, 'MAIN MENU', {
      fontFamily: 'Arial, sans-serif',
      fontSize: '16px',
      color: '#f8fafc',
      fontStyle: 'bold'
    }).setOrigin(0.5);

    menuBtn.add([mBg, mTxt]);

    const onMenu = () => {
      this.scene.start('MenuScene');
    };

    mBg.on('pointerdown', onMenu);
    mTxt.setInteractive({ useHandCursor: true }).on('pointerdown', onMenu);

    // Global tap-to-retry with safe cooldown (so player can tap anywhere to restart)
    this.time.delayedCall(450, () => {
      this.input.on('pointerdown', (pointer) => {
        // If clicking below retry area (shop or menu), don't trigger quick retry
        if (pointer.y > height - 195) return;
        onRetry();
      });
    });

    // Keyboard bindings
    this.input.keyboard.on('keydown-SPACE', onRetry);
    this.input.keyboard.on('keydown-ENTER', onRetry);
  }
}
